// src/authpages/timetables/CalendarView.js
import React, { useState, useEffect } from 'react';
import { Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const CalendarView = ({ events = [] }) => {
  const [viewType, setViewType] = useState('timeGridWeek');
  const [currentDate, setCurrentDate] = useState(new Date());
  const calendarRef = React.createRef();

  // This useEffect will ensure the calendar actually changes view when viewType changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(viewType);
    }
  }, [viewType]);

  // Debug log for events - this helps debug color issues
  useEffect(() => {
    if (events.length > 0) {
      console.log('Calendar received events:', events);
    }
  }, [events]);

  const handleViewChange = (newView) => {
    setViewType(newView);
  };

  const handlePrev = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.prev();
    setCurrentDate(calendarApi.getDate());
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.next();
    setCurrentDate(calendarApi.getDate());
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.today();
    setCurrentDate(calendarApi.getDate());
  };

  const getFormattedDate = () => {
    if (viewType === 'dayGridMonth') {
      // Month view - show month and year
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(currentDate);
    } else if (viewType === 'timeGridWeek') {
      // Week view - show date range
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) return '';

      const start = calendarApi.view.activeStart;
      const end = calendarApi.view.activeEnd;
      end.setDate(end.getDate() - 1); // Adjust end date to be the last day of the view

      return `${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(start)} - ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(end)}`;
    } else {
      // Day view - show full date
      return new Intl.DateTimeFormat('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }).format(currentDate);
    }
  };

  // Format events for FullCalendar - FIXED to properly use event colors
  const formattedEvents = events.map(event => {
    // Use the event's color directly, or fall back to a default
    // This ensures subject colors are preserved for Study Schedule events
    const eventColor = event.color || '#4285F4'; // Default to blue only if no color is provided
    
    return {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      // Use multiple color properties to ensure one works
      backgroundColor: eventColor,
      borderColor: eventColor,
      color: eventColor, // Some versions of FullCalendar use this
      textColor: getTextColor(eventColor),
      // Store the original event in extendedProps for reference
      extendedProps: {
        provider: event.provider,
        originalColor: event.color,
        originalEvent: event
      }
    };
  });

  // Helper function to determine text color based on background darkness
  function getTextColor(bgColor) {
    // Default to white if no color is provided
    if (!bgColor) return '#FFFFFF';
    
    // Convert hex to RGB
    let r, g, b;
    
    if (bgColor.startsWith('#')) {
      const hex = bgColor.substring(1);
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (bgColor.startsWith('rgb')) {
      const rgb = bgColor.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        [r, g, b] = rgb.map(Number);
      } else {
        return '#FFFFFF';
      }
    } else {
      return '#FFFFFF';
    }
    
    // Calculate brightness (higher = lighter)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return white for dark backgrounds, black for light backgrounds
    return brightness > 128 ? '#000000' : '#FFFFFF';
  }

  return (
    <div className="calendar-container">
      <div className="px-6 py-4 border-b border-gray-200">
        <Row className="align-items-center">
          <Col md={4}>
            <div className="flex items-center">
              <CalendarIcon className="text-blue-600 mr-2" size={20} />
              <h5 className="mb-0 font-medium">Calendar</h5>
            </div>
          </Col>
          <Col md={4} className="text-center">
            <div className="flex items-center justify-center">
              <Button 
                variant="link" 
                className="p-0 mr-3 text-blue-600" 
                onClick={handlePrev}
              >
                <ChevronLeft size={20} />
              </Button>
              <h6 className="mb-0 font-semibold">{getFormattedDate()}</h6>
              <Button 
                variant="link" 
                className="p-0 ml-3 text-blue-600" 
                onClick={handleNext}
              >
                <ChevronRight size={20} />
              </Button>
            </div>
          </Col>
          <Col md={4} className="text-end">
            <ButtonGroup className="shadow-sm">
              <Button 
                variant={viewType === 'dayGridMonth' ? 'primary' : 'outline-primary'} 
                onClick={() => handleViewChange('dayGridMonth')}
                className="py-1 px-3"
              >
                Month
              </Button>
              <Button 
                variant={viewType === 'timeGridWeek' ? 'primary' : 'outline-primary'} 
                onClick={() => handleViewChange('timeGridWeek')}
                className="py-1 px-3"
              >
                Week
              </Button>
              <Button 
                variant={viewType === 'timeGridDay' ? 'primary' : 'outline-primary'} 
                onClick={() => handleViewChange('timeGridDay')}
                className="py-1 px-3"
              >
                Day
              </Button>
            </ButtonGroup>
            <Button 
              variant="outline-secondary" 
              className="ml-2 py-1 px-3" 
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>
        </Row>
      </div>
      <div className="p-6">
        <div style={{ height: '600px' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={viewType} // Set initial view type
            headerToolbar={false} // We're creating our own header
            events={formattedEvents}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false,
              hour12: false
            }}
            allDaySlot={true}
            slotDuration="00:30:00"
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            height="100%"
            firstDay={1} // Start the week on Monday
            
            // Configure each view type
            views={{
              dayGridMonth: {
                // Month view settings
                titleFormat: { year: 'numeric', month: 'long' },
                dayHeaderFormat: { weekday: 'short' },
                dayMaxEvents: 3,
                fixedWeekCount: false
              },
              timeGridWeek: {
                // Week view settings
                titleFormat: { 
                  year: 'numeric',
                  month: 'short', 
                  day: 'numeric'
                },
                dayHeaderFormat: { 
                  weekday: 'short', 
                  month: 'numeric', 
                  day: 'numeric', 
                  omitCommas: true 
                }
              },
              timeGridDay: {
                // Day view settings
                titleFormat: { 
                  weekday: 'long',
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                },
                dayHeaderFormat: { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric', 
                  omitCommas: true 
                }
              }
            }}
            eventContent={(eventInfo) => (
              <div className="fc-event-content">
                <div className="fc-event-title font-medium">
                  {eventInfo.event.title}
                </div>
                {eventInfo.event.extendedProps.provider && (
                  <div className="fc-event-provider text-xs opacity-80">
                    {eventInfo.event.extendedProps.provider}
                  </div>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default CalendarView;